import React, { ReactNode, useState, useMemo } from "react";
import { Control, Controller, DeepMap, FieldError, RegisterOptions } from "react-hook-form";
import { fieldError } from "../../../../utils/functions";
import classNames from "classnames";
import { format, parse, set } from "date-fns";
import {
  AccessibleDropdown,
  AccessibleDropdownOption,
  CustomMenu,
  CustomValueContainer,
} from "../../../../components/global_components/form_components/accessible/AccessibleDropdown";
import { components, MultiValue, SingleValue } from "react-select";
import { uniqBy } from "lodash";
import { TZDate } from "@date-fns/tz";
import { useTimezone } from "../../../../hooks/useTimezone";

type Transform<T> = {
  input: (value: T | null | undefined) => Date | string | null | undefined;
  output: (value: Date | string | null | undefined) => T | null | undefined;
};

type SharedTimePickerProps = {
  id?: string;
  readonly?: boolean;
  label?: ReactNode;
  required?: boolean;
  className?: string;
  clearable?: boolean;
  placeholder?: string;
  valueFormat?: "time" | "datetime";
  useClientTimezone?: boolean; // Only useful for valueFormat="datetime"
  showTimezoneIfDifferent?: boolean;
  transform?: Transform<any>;
};

type ControlledTimePickerProps<FormValue, Key extends string> = SharedTimePickerProps & {
  control: Control;
  name: string;
  defaultValue?: Date | string;
  errors?: DeepMap<Record<Key, FormValue>, FieldError>;
  rules?: Exclude<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

export function ControlledTimePicker<FormValue, Key extends string>(props: ControlledTimePickerProps<FormValue, Key>) {
  const {
    id,
    control,
    name,
    label,
    readonly = false,
    errors,
    required = false,
    rules,
    className,
    clearable = false,
    defaultValue,
    placeholder,
    valueFormat = "time",
    useClientTimezone,
    transform,
    showTimezoneIfDifferent,
  } = props;

  const error = fieldError(errors, name);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules ?? {}}
      render={({ onChange, value }) => {
        return (
          <TimePicker
            id={id}
            readonly={readonly}
            value={value}
            onChange={onChange}
            label={label}
            required={required}
            error={error?.content.message}
            className={className}
            clearable={clearable}
            placeholder={placeholder}
            valueFormat={valueFormat}
            useClientTimezone={useClientTimezone}
            transform={transform}
            showTimezoneIfDifferent={showTimezoneIfDifferent}
          />
        );
      }}
    />
  );
}

type TimePickerProps = SharedTimePickerProps & {
  readonly?: boolean;
  error?: string;
  value?: Date | string;
  onChange?: ((value?: Date | string) => void) | undefined;
};

// Generate time options in 30-minute increments starting from 9 AM
function generateTimeOptions(): AccessibleDropdownOption[] {
  const options: AccessibleDropdownOption[] = [];
  const startHour = 9; // Start at 9 AM
  const endHour = 23; // End at 11 PM

  for (let hour = startHour; hour <= endHour; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = set(new Date(), { hours: hour, minutes: minute, seconds: 0, milliseconds: 0 });
      const label = format(time, "h:mm a");
      options.push({
        value: label,
        label: label,
        key: `${hour}-${minute}`,
      });
    }
  }

  // Add early morning hours at the end
  for (let hour = 0; hour < startHour; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = set(new Date(), { hours: hour, minutes: minute, seconds: 0, milliseconds: 0 });
      const label = format(time, "h:mm a");
      options.push({
        value: label,
        label: label,
        key: `${hour}-${minute}`,
      });
    }
  }

  return options;
}

function parseFlexibleTime(input: string): string | null {
  if (!input) return null;

  let normalized = input.trim().toLowerCase();
  normalized = normalized.replace(/[.,;\-\s]+/g, ":");

  const amPmMatch = normalized.match(/(a|am|p|pm)$/);
  const isPM = amPmMatch && (amPmMatch[1] === "p" || amPmMatch[1] === "pm");
  const isAM = amPmMatch && (amPmMatch[1] === "a" || amPmMatch[1] === "am");

  normalized = normalized.replace(/(a|am|p|pm)$/, "").trim();

  const digitsOnly = normalized.replace(/\D/g, "");

  let hours: number;
  let minutes: number = 0;

  if (digitsOnly.length === 1) {
    // Single digit: 9 → 9:00
    hours = parseInt(digitsOnly, 10);
  } else if (digitsOnly.length === 2) {
    // Two digits: could be hour (10) or hour+minute (95 → 9:05)
    const num = parseInt(digitsOnly, 10);
    if (num <= 12 || num === 24) {
      hours = num;
    } else {
      // Treat as hour + single digit minute (95 → 9:05)
      hours = Math.floor(num / 10);
      minutes = num % 10;
    }
  } else if (digitsOnly.length === 3) {
    // Three digits: 905 → 9:05, 130 → 1:30
    hours = Math.floor(parseInt(digitsOnly, 10) / 100);
    minutes = parseInt(digitsOnly.slice(1), 10);
  } else if (digitsOnly.length === 4) {
    // Four digits: 1345 → 13:45 or 1:45 PM depending on format
    hours = parseInt(digitsOnly.slice(0, 2), 10);
    minutes = parseInt(digitsOnly.slice(2), 10);
  } else {
    // Check if it's already in h:mm or hh:mm format
    const parts = normalized.split(":").filter((p) => p.length > 0);
    if (parts.length === 2) {
      hours = parseInt(parts[0], 10);
      minutes = parseInt(parts[1], 10);
    } else {
      return null;
    }
  }

  if (isNaN(hours) || isNaN(minutes) || minutes < 0 || minutes >= 60) {
    return null;
  }

  // 12-hour format conversion
  if (isPM && hours < 12) {
    hours += 12;
  } else if (isAM && hours === 12) {
    hours = 0;
  } else if (!isPM && !isAM) {
    // No AM/PM specified - use 12-hour format assumption for hours <= 12
    if (hours <= 12 && hours !== 0) {
      // Assume AM for early hours, PM for afternoon
      if (hours >= 1 && hours <= 6) hours += 12; // 1-6 without AM/PM likely means PM
      // 7-12 without AM/PM likely means AM (keep as is)
    }
  }

  if (hours === 24) hours = 0;
  if (hours < 0 || hours >= 24) return null;

  const time = set(new Date(), {
    hours: hours,
    minutes: minutes,
    seconds: 0,
    milliseconds: 0,
  });

  return format(time, "h:mm a");
}

export function parseAmPmTime(value: string): [number, number] {
  const parts = value.split(":");
  const hour = +parts[0];
  const minute = parts[1];
  if (isNaN(+minute)) {
    // It might have AM/PM attached
    const ampm = minute?.toLowerCase().includes("pm") ? "pm" : "am";
    const withoutAmPm = minute?.toLowerCase().replace("am", "").replace("pm", "");
    const actualMinute = !isNaN(+withoutAmPm) ? +withoutAmPm : 0;
    if (ampm === "pm" && hour < 12) return [hour + 12, actualMinute];
    if (ampm === "am" && hour === 12) return [0, actualMinute];
    return [hour, actualMinute];
  }
  return [hour, +minute];
}

// Convert value to internal time string format ("h:mm a")
function valueToTimeString(
  value: string | Date,
  valueFormat: "time" | "datetime",
  timezone: string = "UTC",
): string | null | undefined {
  if (!value) return value;

  if (valueFormat === "datetime") {
    if (typeof value === "string") {
      const parsed = parseFlexibleTime(value);
      if (parsed) return parsed;

      const date = new TZDate(value, timezone);
      if (!isNaN(date.getTime())) {
        return format(date, "h:mm a");
      }
    } else if (value instanceof Date) {
      return format(new TZDate(value, timezone), "h:mm a");
    }
    return null;
  } else {
    // valueFormat === "time"
    if (typeof value === "string") {
      return value;
    } else if (value instanceof Date) {
      return format(value, "h:mm a");
    }
    return null;
  }
}

// Convert time string to output value format
function timeStringToValue(
  timeString: string | null | undefined,
  valueFormat: "time" | "datetime",
  timezone: string = "UTC",
): Date | string | null | undefined {
  if (!timeString) return timeString;

  if (valueFormat === "datetime") {
    const [hour, minute] = parseAmPmTime(timeString);
    // Return the current date with the selected time
    const now = new TZDate(new Date(), timezone);
    const dateTime = set(now, { hours: hour, minutes: minute, seconds: 0, milliseconds: 0 });
    return dateTime;
  } else {
    // valueFormat === "time"
    return timeString;
  }
}

export const TimePicker = ({
  id,
  readonly = false,
  error,
  value,
  onChange,
  label,
  required = false,
  className,
  clearable = false,
  placeholder = "--:-- --",
  valueFormat = "time",
  useClientTimezone,
  showTimezoneIfDifferent = false,
  transform = { input: (v) => v, output: (v) => v },
}: TimePickerProps) => {
  const [addedOptions, setAddedOptions] = useState<AccessibleDropdownOption[]>([]);
  const timeOptions = useMemo(() => {
    const allOptions = uniqBy([...generateTimeOptions(), ...addedOptions], "value").sort((a, b) => {
      const dateA = parseFlexibleTime(a.value as string);
      const dateB = parseFlexibleTime(b.value as string);
      if (!dateA || !dateB) return 0;
      const parsedA = parse(dateA, "h:mm a", new Date());
      const parsedB = parse(dateB, "h:mm a", new Date());
      return parsedA.getHours() * 60 + parsedA.getMinutes() - (parsedB.getHours() * 60 + parsedB.getMinutes());
    });
    const nineAmIdx = allOptions.findIndex((opt) => parseFlexibleTime(opt.value as string) === "9:00 AM");
    if (nineAmIdx > 0) return [...allOptions.slice(nineAmIdx), ...allOptions.slice(0, nineAmIdx)];
    return allOptions;
  }, [addedOptions]);

  const transformedInput = transform.input(value);

  const {
    clientTimezoneInfo: { timezone },
    agencyTimezoneInfo,
    clientIsInAgencyTimezone,
  } = useTimezone();
  const formatTimezone = useClientTimezone ? timezone : "UTC";

  const showTimezone = showTimezoneIfDifferent && !useClientTimezone && !clientIsInAgencyTimezone;
  const agencyTimezoneAbbr = (() => {
    const timezone = agencyTimezoneInfo?.timezone;
    if (!timezone) return null;
    const formatter = new Intl.DateTimeFormat("en-US", { timeZone: timezone, timeZoneName: "short" });
    const parts = formatter.formatToParts(new Date());
    const tzPart = parts.find((part) => part.type === "timeZoneName");
    return tzPart ? tzPart.value : null;
  })();

  const handleChange = (option: AccessibleDropdownOption | null) => {
    const outputValue = timeStringToValue(option?.value as string | undefined, valueFormat, formatTimezone);
    onChange?.(transform.output(outputValue ?? undefined));
  };

  if (readonly) {
    const timeString = transformedInput && valueToTimeString(transformedInput, valueFormat, formatTimezone);
    let displayValue = "";
    if (timeString) {
      displayValue = timeString;
      if (showTimezone && agencyTimezoneAbbr) {
        displayValue += ` ${agencyTimezoneAbbr}`;
      }
    }

    return (
      <>
        {label && <label>{label}</label>}
        <span>{displayValue}</span>
      </>
    );
  }

  const TimePickerValueContainer: CustomValueContainer = (props) => {
    return (
      <div className='tw-flex tw-items-center tw-gap-gg-1'>
        <components.ValueContainer {...props}>{props.children}</components.ValueContainer>
        {showTimezone && agencyTimezoneAbbr && props.hasValue && (
          <span className='tw-inline tw-text-[14px] tw-tracking-[0.15px] tw-text-primary-hint'>
            {agencyTimezoneAbbr}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={classNames(className, "tw-flex tw-flex-col")}>
      {label && (
        <label className='label-text tw-mb-1'>
          {label}
          {required && <span className='tw-ml-1 tw-text-red'>*</span>}
        </label>
      )}
      <AccessibleDropdown
        id={id}
        name='time'
        value={(transformedInput && valueToTimeString(transformedInput, valueFormat, formatTimezone)) || undefined}
        onChange={
          handleChange as (
            newValue: SingleValue<AccessibleDropdownOption<any>> | MultiValue<AccessibleDropdownOption<any>>,
          ) => void
        }
        options={timeOptions}
        placeholder={placeholder}
        clearable={clearable}
        allowAdditions
        menuPlacement='auto'
        onCreateOption={(input) => {
          const parsed = parseFlexibleTime(input);
          if (!parsed) return;
          const newOption = {
            value: parsed,
            label: parsed,
            key: `added-${parsed}`,
          };
          setAddedOptions((prev) => [...prev, newOption]);
          const outputValue = timeStringToValue(newOption.value, valueFormat, formatTimezone);
          onChange?.(transform.output(outputValue ?? undefined));
        }}
        transform={{
          input: (value: Date | string | undefined | null): AccessibleDropdownOption | null => {
            if (!value) return null;

            let timeString: string | null = null;
            if (value instanceof Date) {
              timeString = format(value, "h:mm a");
            } else if (typeof value === "string") {
              const parsed = parseFlexibleTime(value);
              timeString = parsed || value;
            }
            if (!timeString) return null;
            // It's an option
            return {
              value: timeString,
              label: timeString,
            };
          },
          output: (option) => option,
        }}
        components={{
          menu: TimePickerMenu,
          valueContainer: TimePickerValueContainer,
        }}
        error={error}
        showErrorLabel
      />
    </div>
  );
};

const TimePickerMenu: CustomMenu = (props) => {
  return (
    <components.Menu
      {...props}
      className='tw-rounded-sm tw-border-[1px] tw-border-solid tw-border-border tw-shadow-[0_2px_12px_0_rgba(55,65,81,0.15)]'
    >
      {props.children}
    </components.Menu>
  );
};
